'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { PlanRequest } from '@/lib/db/schemas/requests'
import type { Serialized } from '@/lib/db/mappers'
import { fetchApi } from '@/lib/utils/fetch-api'
import { parseQueryErrorMessage } from '@/lib/api/parse-query-error'
import { designRequestKeys } from './use-design-requests'

type ApiPlanRequest = Serialized<PlanRequest>

// ============================================
// Filter Types
// ============================================
export interface PlanRequestFilterOptions {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  status?: string
  customerId?: string
  designOffice?: string
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasMore: boolean
}

type PlanRequestStatus = NonNullable<ApiPlanRequest['status']>

// ============================================
// Query Keys
// ============================================
export const planRequestKeys = {
  all: ['planRequests'] as const,
  lists: () => [...planRequestKeys.all, 'list'] as const,
  list: (filters: PlanRequestFilterOptions) => [...planRequestKeys.lists(), filters] as const,
  details: () => [...planRequestKeys.all, 'detail'] as const,
  detail: (id: string) => [...planRequestKeys.details(), id] as const,
  byCustomer: (customerId: string) => [...planRequestKeys.all, 'customer', customerId] as const,
  counts: () => [...planRequestKeys.all, 'counts'] as const,
}

// ============================================
// Helper: Build query string from filters
// ============================================
function buildPlanRequestParams(filters: PlanRequestFilterOptions): string {
  const params = new URLSearchParams()
  if (filters.page != null) params.set('page', String(filters.page))
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)
  if (filters.status) params.set('status', filters.status)
  if (filters.customerId) params.set('customerId', filters.customerId)
  if (filters.designOffice) params.set('designOffice', filters.designOffice)
  return params.toString()
}

// ============================================
// Hooks
// ============================================

/**
 * ページネーション付きプラン依頼一覧取得
 */
export function usePlanRequests(filters: PlanRequestFilterOptions = {}) {
  return useQuery({
    queryKey: planRequestKeys.list(filters),
    queryFn: async () => {
      const qs = buildPlanRequestParams(filters)
      const res = await fetch(`/api/plan-requests?${qs}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) {
        throw new Error(await parseQueryErrorMessage(res))
      }
      const json = await res.json()
      if (json.pagination) {
        return {
          data: json.data,
          total: json.pagination.total ?? 0,
          page: json.pagination.page ?? 1,
          limit: json.pagination.limit ?? 50,
          totalPages: json.pagination.totalPages ?? 1,
          hasMore: json.pagination.hasMore ?? false,
        } as PaginatedResult<ApiPlanRequest>
      }
      const data = json.data ?? json
      // 200 でも配列でない応答は契約崩れ=障害。[] に潰すと画面が「0件」に化けて
      // 失敗を隠す(偽0・use-g-plans PR #8941 と同型)。error に倒して isError に載せる。
      if (!Array.isArray(data)) {
        throw new Error('プラン依頼一覧の応答形式が不正です（配列ではありません）')
      }
      return {
        data,
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
        hasMore: false,
      } as PaginatedResult<ApiPlanRequest>
    },
    placeholderData: (previousData) => previousData,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * ステータス別プラン依頼取得
 */
export function usePlanRequestsByStatus(status: PlanRequestStatus) {
  return useQuery({
    queryKey: planRequestKeys.list({ status }),
    queryFn: () =>
      fetchApi<ApiPlanRequest[]>(
        `/api/plan-requests?paginated=false&status=${encodeURIComponent(status)}`
      ),
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * 顧客別だけは、削除した社員の**氏名**が余分に付いてくる（`getByCustomer` が join する）。
 * 2026-09-04: 行を開いた時の「中止しました／いつ・誰が・なぜ」で使う。
 * 他の一覧（管理画面・営業事務）は従来どおり付かないので、ここだけの型にする。
 */
export type ApiPlanRequestByCustomer = ApiPlanRequest & { deletedByName?: string | null }

/**
 * 顧客別プラン依頼取得
 */
export function usePlanRequestsByCustomer(customerId: string) {
  return useQuery({
    queryKey: planRequestKeys.byCustomer(customerId),
    queryFn: () =>
      fetchApi<ApiPlanRequestByCustomer[]>(
        `/api/plan-requests?paginated=false&customerId=${customerId}`
      ),
    enabled: !!customerId,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * 単一プラン依頼取得
 */
export function usePlanRequest(id: string) {
  return useQuery({
    queryKey: planRequestKeys.detail(id),
    queryFn: () => fetchApi<ApiPlanRequest>(`/api/plan-requests/${id}`),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * ステータスカウント取得
 * Computes status counts from full plan request list
 */
export function usePlanRequestStatusCounts() {
  return useQuery({
    queryKey: planRequestKeys.counts(),
    queryFn: async () => {
      const requests = await fetchApi<ApiPlanRequest[]>('/api/plan-requests?paginated=false')
      const counts: Record<string, number> = {}
      requests.forEach((r) => {
        const status = r.status ?? 'unknown'
        counts[status] = (counts[status] || 0) + 1
      })
      return counts as Record<PlanRequestStatus, number>
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * プラン依頼作成
 */
export function useCreatePlanRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: Partial<ApiPlanRequest>) =>
      fetchApi<ApiPlanRequest>('/api/plan-requests', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: (newRequest) => {
      queryClient.setQueryData(planRequestKeys.detail(newRequest.id), newRequest)
      queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
      queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
      if (newRequest.customerId) {
        queryClient.invalidateQueries({
          queryKey: planRequestKeys.byCustomer(newRequest.customerId),
        })
      }
    },
  })
}

/**
 * プラン依頼更新の mutation 変数。
 * updates には楽観ロック用の expectedUpdatedAt（更新前の updated_at）を任意で含められる。
 */
export type UpdatePlanRequestVariables = {
  id: string
  updates: Partial<ApiPlanRequest> & { expectedUpdatedAt?: string }
}

/**
 * プラン依頼更新
 */
export function useUpdatePlanRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: UpdatePlanRequestVariables) => {
      // 🔴 呼び出し側が token を入れ忘れても必ず載せる。
      // `/request-management/design` のスプシ列インライン編集は `{ [field]: value }` だけを
      // 送っており、`checkVersion` が未指定を「一致」として通すため last-write-wins だった
      // （Codex システム全体レビュー P1・顧客/契約で塞いだのと同型）。
      const cached = queryClient.getQueryData<ApiPlanRequest>(planRequestKeys.detail(id))
      let token = updates.expectedUpdatedAt ?? (cached?.updatedAt as string | undefined)
      if (!token) {
        const fresh = await fetchApi<ApiPlanRequest>(`/api/plan-requests/${id}`)
        token = (fresh.updatedAt as string | undefined) ?? undefined
      }
      if (!token) {
        throw new Error(
          '楽観ロック token (expectedUpdatedAt) が不足しています。GET でプラン依頼を取得してから更新してください'
        )
      }
      // 409 は自動再送しない（顧客側 owner 判断 A と同じ形）。
      // 判断材料: docs/運用/同時編集-lost-update-owner判断-2026-08-12.md
      return fetchApi<ApiPlanRequest>(`/api/plan-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...updates, expectedUpdatedAt: token }),
      })
    },
    onMutate: async ({ id, updates }: UpdatePlanRequestVariables) => {
      await queryClient.cancelQueries({ queryKey: planRequestKeys.detail(id) })

      const previousRequest = queryClient.getQueryData<ApiPlanRequest>(planRequestKeys.detail(id))

      if (previousRequest) {
        // expectedUpdatedAt は楽観ロック用のバージョンメタ（実フィールドではない）。
        // 楽観更新のキャッシュ実体に混入させない。
        const { expectedUpdatedAt: _expectedUpdatedAt, ...fieldUpdates } = updates
        // 🔴 updatedAt は触らない。フロント時刻を書き込むと次の mutationFn がそれを
        // token として拾い、サーバの実 updated_at と必ずズレて偽 409 になる
        // （顧客 #5821 / 契約と同型・Codex システム全体レビュー P2）。
        // `...fieldUpdates` の後に明示的に戻すのは、呼び出し側が updates に updatedAt を
        // 混ぜても token を汚染させないため。
        queryClient.setQueryData(planRequestKeys.detail(id), {
          ...previousRequest,
          ...fieldUpdates,
          updatedAt: previousRequest.updatedAt,
        })
      }

      return { previousRequest }
    },
    // 🔴 保存成功のたびにサーバ応答を反映する。無いと連続保存の 2 回目が旧 token を送り
    // 「自分の直前の保存」に対して 409 になる（onSettled の再取得は待たれない）。
    // 置換ではなく merge。PATCH は GET が付与する表示用フィールドまでは返さないため。
    onSuccess: (updated, { id }) => {
      if (!updated?.updatedAt) return
      queryClient.setQueryData(planRequestKeys.detail(id), (prev: ApiPlanRequest | undefined) =>
        prev ? { ...prev, ...updated } : updated
      )
    },
    onError: (err, variables, context) => {
      if (context?.previousRequest) {
        queryClient.setQueryData(planRequestKeys.detail(variables.id), context.previousRequest)
      }
    },
    onSettled: (data, _, { id }) => {
      queryClient.invalidateQueries({ queryKey: planRequestKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
      queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
      // 2026-07-31 [業務承認: matsushima-y] プラン依頼管理一覧(/request-management/design)は
      // designRequests クエリ（enriched select が plan_requests を leftJoin）で表示されるため、
      // plan_requests の更新（備考 sheetMemo 等のスプシ列インライン編集）を一覧へ即時反映するには
      // designRequests 側の invalidate も必要。欠けていると staleTime 内は再フェッチされず
      // 「保存されない」に見える（2026-07-31 owner 実機・本番実害）。
      queryClient.invalidateQueries({ queryKey: designRequestKeys.all })
    },
  })
}

/**
 * プラン依頼削除
 */
export function useDeletePlanRequest() {
  const queryClient = useQueryClient()

  // 2026-08-24 [業務承認: 西野さん(owner)]「（削除理由は）必須にして」。
  //   理由は API 側でも parseDeleteReason で必須検証する（片方だけ緩くしない）。
  //   引数を {id, reason} に変えたのは、id だけ渡す旧呼び出しを型で落として
  //   「理由なしで消せる経路」が残らないようにするため。
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      fetchApi<void>(`/api/plan-requests/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({ queryKey: planRequestKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: planRequestKeys.lists() })
      queryClient.invalidateQueries({ queryKey: planRequestKeys.counts() })
      // 2026-07-31 [業務承認: matsushima-y] 保存系網羅監査 NG-9c: プラン依頼管理一覧の
      // 孤児行削除はこの hook 経由だが designRequests を invalidate しておらず、
      // toast.success が出るのに一覧から行が消えなかった（useUpdatePlanRequest の
      // #12108 是正と同型の横展開漏れ）。
      queryClient.invalidateQueries({ queryKey: designRequestKeys.all })
    },
  })
}
